import React, { useEffect, useState } from 'react';
import { white } from './components/colors';
import { UserLicense, LicenseType } from './types';
import { useUserLicensesQuery } from './queries';
import { Table, TableProps } from 'antd';
import dayjs from 'dayjs';
import { Button } from './ui/Button';
import { SectionTransparent } from './ui/SectionTransparent';
import { TextInput } from './ui/TextInput';
import { useSubmitUserLicenseMutation } from './mutations';
import { styled } from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  color: ${white};
  overflow-y: auto;
  align-items: center;
  gap: 48px;
  padding-top: 72px;
  padding-bottom: 72px;
`;

const SectionContainerTransparent = styled(SectionTransparent)`
  max-width: 800px;
  text-align: center;
  font-size: 1.3rem;
  gap: 16px;
  width: 100%;
`;

const StyledTextInput = styled(TextInput)`
  width: 100%;
`;

const NameContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  flex-direction: row;
  @media (max-width: 791px) {
    flex-direction: column;
    width: 100%;
    align-items: center;
  }
`;

const defaultUserLicenseState: Partial<UserLicense> = {
  maxLicenseQuota: 1,
  licenseType: LicenseType.CLUB,
  isFreeApp: true,
};

export const CustomerContainer: React.FC = () => {
  const [mode, setMode] = useState<'viewing' | 'creating'>('viewing');
  const [userLicense, setUserLicense] = useState<Partial<UserLicense>>(
    defaultUserLicenseState,
  );

  const {
    data: licenses,
    isLoading: isLicensesLoading,
    isError: isLoadingError,
    refetch: refetchLicenses,
  } = useUserLicensesQuery();

  const {
    mutateAsync: submitUserLicense,
    isSuccess: isSubmitUserLicenseSuccess,
  } = useSubmitUserLicenseMutation();

  useEffect(() => {
    const fetchLicenses = async () => {
      await refetchLicenses();
    };
    if (isSubmitUserLicenseSuccess) {
      setUserLicense(defaultUserLicenseState);
      setMode('viewing');
      fetchLicenses();
    }
  }, [isSubmitUserLicenseSuccess]);

  const onCreateLicense = async () => {
    if (!userLicense.email || !userLicense.firstName || !userLicense.lastName) {
      return;
    }

    // Here you would typically call an API to create the license
    await submitUserLicense(userLicense as UserLicense);
  };

  const columns: TableProps<UserLicense>['columns'] = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      defaultSortOrder: 'descend',
      filterSearch: true,
      filterMode: 'menu',
      onFilter: (value, record) => record.email.includes(value as string),
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Club',
      dataIndex: 'club',
      key: 'club',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      defaultSortOrder: 'descend',
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      render: (createdAt) => {
        return dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: 'License Key',
      dataIndex: 'licenseKey',
      key: 'licenseKey',
    },
    {
      title: 'Max. Users',
      dataIndex: 'maxLicenseQuota',
      key: 'maxLicenseQuota',
    },
    {
      title: 'License Valid For',
      dataIndex: 'licenseValidFor',
      key: 'licenseValidFor',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => 'No actions yet',
    },
  ];
  return (
    <Container>
      {mode === 'viewing' && isLicensesLoading && <div>{`Loading...`}</div>}
      {mode === 'viewing' && isLoadingError && (
        <div>{`Error loading licenses`}</div>
      )}
      {mode === 'viewing' && licenses && (
        <>
          <Button onClick={() => setMode('creating')}>Add new license</Button>
          <Table columns={columns} dataSource={licenses} />
        </>
      )}
      {mode === 'creating' && (
        <SectionContainerTransparent title="Creating a new license">
          <StyledTextInput
            label="Email"
            type="email"
            placeholder="Enter your email address..."
            onChange={(emailChange) => {
              setUserLicense({ ...userLicense, email: emailChange });
            }}
            value={userLicense.email || ''}
            required
            // onValidityChange={setEmailValidity}
            transparent={false}
          />
          <NameContainer>
            <StyledTextInput
              label="First Name"
              placeholder="Enter your first name..."
              onChange={(newFirstName) => {
                setUserLicense({ ...userLicense, firstName: newFirstName });
              }}
              required
              // onValidityChange={setFirstNameValidity}
              value={userLicense.firstName || ''}
              transparent={false}
            />
            <StyledTextInput
              label="Last Name"
              placeholder="Enter your last name..."
              onChange={(newLastName) => {
                setUserLicense({ ...userLicense, lastName: newLastName });
              }}
              value={userLicense.lastName || ''}
              required
              // onValidityChange={setLastNameValidity}
              transparent={false}
            />
          </NameContainer>
          <StyledTextInput
            label="Club"
            placeholder="Enter the name of the club..."
            onChange={(club) => {
              setUserLicense({ ...userLicense, club });
            }}
            value={userLicense.club || ''}
            transparent={false}
          />
          <StyledTextInput
            label="Max User Quota"
            placeholder="Enter how many users can use this license..."
            onChange={(newMaxUsers) => {
              setUserLicense({
                ...userLicense,
                maxLicenseQuota: parseInt(newMaxUsers, 10) || 0,
              });
            }}
            value={userLicense.maxLicenseQuota?.toString()}
            transparent={false}
          />
          <StyledTextInput
            label="License validity"
            placeholder="Enter how much time this license is valid for..."
            onChange={(newLicenseDuration) => {
              setUserLicense({
                ...userLicense,
                licenseValidFor: newLicenseDuration,
              });
            }}
            value={userLicense.licenseValidFor || ''}
            transparent={false}
          />
          <Button
            large
            // disabled={!emailValidity}
            onClick={async () => {
              await onCreateLicense();
            }}
          >
            {'Create License'}
          </Button>
        </SectionContainerTransparent>
      )}
    </Container>
  );
};
